import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "./components/pursue/Header";
import StatsRow from "./components/pursue/StatsRow";
import GlobalMap from "./components/pursue/GlobalMap";
import TimelineChart from "./components/pursue/TimelineChart";
import FilterBar from "./components/pursue/FilterBar";
import CaseTable from "./components/pursue/CaseTable";
import CaseDetailDialog from "./components/pursue/CaseDetailDialog";
import AIChat from "./components/pursue/AIChat";
import ArchiveView from "./components/pursue/ArchiveView";
import SubscribeBanner from "./components/pursue/SubscribeBanner";
import UserMenu from "./components/pursue/UserMenu";
import { fetchCases, fetchStats } from "./lib/api";
import { FolderArchive } from "lucide-react";
import "./App.css";

// === Deep-link helpers ===
function readUrlState() {
  const sp = new URLSearchParams(window.location.search);
  return {
    search: sp.get("q") || "",
    agency: sp.get("agency") || null,
    type: sp.get("type") || null,
    country: sp.get("country") || null,
    classification: sp.get("classification") || null,
    year: sp.get("year") ? parseInt(sp.get("year")) : null,
    case: sp.get("case") || null,
    view: sp.get("view") || "dashboard",
    archiveCase: sp.get("archiveCase") || null,
  };
}

function writeUrlState(state) {
  const sp = new URLSearchParams();
  if (state.search) sp.set("q", state.search);
  if (state.agency) sp.set("agency", state.agency);
  if (state.type) sp.set("type", state.type);
  if (state.country) sp.set("country", state.country);
  if (state.classification) sp.set("classification", state.classification);
  if (state.year) sp.set("year", state.year);
  if (state.case) sp.set("case", state.case);
  if (state.view && state.view !== "dashboard") sp.set("view", state.view);
  if (state.archiveCase) sp.set("archiveCase", state.archiveCase);
  const qs = sp.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, "", url);
}

export default function App() {
  const initial = readUrlState();

  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(initial.search);
  const [agency, setAgency] = useState(initial.agency);
  const [type, setType] = useState(initial.type);
  const [country, setCountry] = useState(initial.country);
  const [classification, setClassification] = useState(initial.classification);
  const [year, setYear] = useState(initial.year);

  const [activeCase, setActiveCase] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [archiveOpen, setArchiveOpen] = useState(initial.view === "archive");
  const [archiveCaseFilter, setArchiveCaseFilter] = useState(initial.archiveCase);
  const [archivePinnedOnly, setArchivePinnedOnly] = useState(false);

  // Stats
  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, []);

  const params = useMemo(
    () => ({
      ...(search ? { search } : {}),
      ...(agency ? { agency } : {}),
      ...(type ? { type } : {}),
      ...(country ? { country } : {}),
      ...(classification ? { classification } : {}),
      ...(year ? { year } : {}),
    }),
    [search, agency, type, country, classification, year]
  );

  // Cases
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchCases(params)
        .then(setCases)
        .finally(() => setLoading(false));
    }, search ? 220 : 0);
    return () => clearTimeout(timer);
  }, [params, search]);

  // Deep-link sync
  useEffect(() => {
    writeUrlState({
      search,
      agency,
      type,
      country,
      classification,
      year,
      case: activeCase?.case_id || null,
      view: archiveOpen ? "archive" : "dashboard",
      archiveCase: archiveCaseFilter,
    });
  }, [search, agency, type, country, classification, year, activeCase, archiveOpen, archiveCaseFilter]);

  // popstate listener — sync state on browser back/forward
  useEffect(() => {
    const onPop = () => {
      const s = readUrlState();
      setSearch(s.search);
      setAgency(s.agency);
      setType(s.type);
      setCountry(s.country);
      setClassification(s.classification);
      setYear(s.year);
      setArchiveOpen(s.view === "archive");
      setArchiveCaseFilter(s.archiveCase);
      if (s.case && (!activeCase || activeCase.case_id !== s.case)) {
        const c = cases.find((x) => x.case_id === s.case);
        if (c) {
          setActiveCase(c);
          setDialogOpen(true);
        }
      } else if (!s.case && dialogOpen) {
        setDialogOpen(false);
        setActiveCase(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, activeCase, dialogOpen]);

  // Auto-open case from URL on first load
  useEffect(() => {
    if (initial.case && cases.length > 0 && !dialogOpen) {
      const c = cases.find((x) => x.case_id === initial.case);
      if (c) {
        setActiveCase(c);
        setDialogOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases]);

  const handleSelect = useCallback((c) => {
    setActiveCase(c);
    setDialogOpen(true);
  }, []);

  const clearAll = () => {
    setSearch("");
    setAgency(null);
    setType(null);
    setCountry(null);
    setClassification(null);
    setYear(null);
  };

  const openArchiveForCase = (caseId) => {
    setArchiveCaseFilter(caseId || null);
    setArchiveOpen(true);
    setDialogOpen(false);
  };

  return (
    <div className="App grid-bg min-h-screen text-zinc-100">
      <Header
        stats={stats}
        rightSlot={
          <>
            <button
              data-testid="open-archive-btn"
              onClick={() => {
                setArchiveCaseFilter(null);
                setArchiveOpen(true);
              }}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 font-mono text-[10px] uppercase tracking-widest text-zinc-300 transition-colors"
            >
              <FolderArchive className="h-3.5 w-3.5" />
              Archive
            </button>
            <UserMenu
              onOpenPinned={() => {
                setArchiveCaseFilter(null);
                setArchivePinnedOnly(true);
                setArchiveOpen(true);
              }}
            />
          </>
        }
      />
      <StatsRow stats={stats} />

      <main className="px-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 border-b border-zinc-800">
          <div className="lg:col-span-2 border-r border-zinc-800">
            <GlobalMap
              cases={cases}
              activeCaseId={activeCase?.case_id}
              onSelect={handleSelect}
            />
          </div>
          <div className="lg:col-span-1">
            <AIChat activeCase={activeCase} />
          </div>
        </div>

        <div className="px-6 py-4 border-b border-zinc-800">
          <TimelineChart
            stats={stats}
            activeYear={year}
            onSelectYear={(y) => setYear(y)}
          />
        </div>

        <FilterBar
          search={search}
          setSearch={setSearch}
          agency={agency}
          setAgency={setAgency}
          type={type}
          setType={setType}
          country={country}
          setCountry={setCountry}
          classification={classification}
          setClassification={setClassification}
          stats={stats}
          onClear={clearAll}
          resultsCount={cases.length}
          cases={cases}
          onRefreshComplete={(r) => {
            if (r?.newly_seeded > 0) {
              fetchStats().then(setStats).catch(() => {});
              fetchCases(params).then(setCases).catch(() => {});
            }
          }}
        />
        <div className="px-6 py-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between">
            <span>
              ◆ Case Registry {loading && <span className="text-amber-400">· loading</span>}
            </span>
            <span>SOURCE · WAR.GOV/UFO + AARO</span>
          </div>
          <CaseTable
            cases={cases}
            onSelect={handleSelect}
            activeCaseId={activeCase?.case_id}
          />
        </div>

        <footer className="border-t border-zinc-800 px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            PURSUE Mirror · Unofficial public viewer of declassified UAP records
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
            Data derived from public releases at WAR.GOV/UFO and AARO.MIL
          </span>
        </footer>
      </main>

      <CaseDetailDialog
        caseData={activeCase}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onOpenArchive={(caseId) => openArchiveForCase(caseId)}
      />

      {archiveOpen && (
        <ArchiveView
          focusCaseId={archiveCaseFilter}
          initialPinnedOnly={archivePinnedOnly}
          onClose={() => {
            setArchiveOpen(false);
            setArchiveCaseFilter(null);
            setArchivePinnedOnly(false);
          }}
        />
      )}

      <SubscribeBanner />
    </div>
  );
}
